/**
 * Shared rules for provider availability (web + mobile booking).
 * Firestore user doc fields used by company manager UI:
 * - available: boolean (manual / auto-updated on leave)
 * - workingHours: { [day]: { isWorking, start, end } }
 * - timeOffPeriods: { id, type, title, startDate, endDate, note? }[]  (YYYY-MM-DD)
 * - onLeaveUntil: string | null  (YYYY-MM-DD end of active leave)
 * - availableBeforeLeave: boolean snapshot when leave auto-disabled provider
 * - pendingGoOnlinePrompt: true when manager cancelled active leave
 * - leaveCancelledAt: timestamp for deduping provider prompt
 */

export interface ProviderTimeOffPeriod {
  id: string;
  type: string;
  title: string;
  startDate: string;
  endDate: string;
  note?: string;
}

export function toDateOnlyString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getActiveTimeOffOnDate(
  periods: ProviderTimeOffPeriod[] | undefined | null,
  date: Date = new Date()
): ProviderTimeOffPeriod | null {
  if (!Array.isArray(periods) || !periods.length) {
    return null;
  }
  const key = toDateOnlyString(date);
  return periods.find((p) => p.startDate <= key && p.endDate >= key) ?? null;
}

/** True when provider is on holiday/leave for the given calendar day. */
export function isProviderOnTimeOff(
  provider: { timeOffPeriods?: ProviderTimeOffPeriod[] } | null | undefined,
  date: Date = new Date()
): boolean {
  return !!getActiveTimeOffOnDate(provider?.timeOffPeriods, date);
}

/** True when manager leave/holiday blocks the provider from going online today. */
export function isProviderOnActiveLeave(
  provider: {
    timeOffPeriods?: ProviderTimeOffPeriod[];
    onLeaveUntil?: string | null;
  } | null | undefined,
  date: Date = new Date()
): boolean {
  return isProviderOnTimeOff(provider, date);
}

export function isProviderBanned(
  provider: { banned?: unknown; isBlocked?: unknown } | null | undefined
): boolean {
  if (!provider) {
    return false;
  }
  const banned = provider.banned;
  const blocked = provider.isBlocked;
  return (
    banned === true ||
    banned === 'true' ||
    banned === 1 ||
    banned === '1' ||
    blocked === true ||
    blocked === 'true' ||
    blocked === 1 ||
    blocked === '1'
  );
}

/**
 * Minimum check before offering a provider in booking / assignment.
 * Extend with workingHours + existing appointment overlap as needed.
 */
export function isProviderGenerallyAvailable(
  provider: {
    available?: boolean;
    banned?: unknown;
    isBlocked?: unknown;
    timeOffPeriods?: ProviderTimeOffPeriod[];
    onLeaveUntil?: string | null;
  } | null | undefined,
  slotDate: Date = new Date()
): boolean {
  if (!provider) {
    return false;
  }
  if (isProviderBanned(provider)) {
    return false;
  }
  if (provider.available === false) {
    return false;
  }
  if (isProviderOnTimeOff(provider, slotDate)) {
    return false;
  }
  return true;
}

export function buildProviderAvailabilityUpdate(
  available: boolean,
  provider: { timeOffPeriods?: ProviderTimeOffPeriod[] } | null | undefined
): Record<string, unknown> {
  const patch: Record<string, unknown> = { available };
  if (available) {
    patch['pendingGoOnlinePrompt'] = false;
    patch['availableBeforeLeave'] = null;
    if (!isProviderOnTimeOff(provider)) {
      patch['onLeaveUntil'] = null;
      patch['onLeaveReason'] = null;
    }
  }
  return patch;
}
